import {
    AfterContentInit,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ContentChildren,
    Directive,
    ElementRef,
    EventEmitter,
    HostBinding,
    Injector,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    Output,
    QueryList,
    TemplateRef,
    ViewChild
} from "@angular/core";
import {AbstractControl, FormBuilder, FormGroup} from "@angular/forms";
import {
    ConfigurationService,
    DataResourceModel,
    EntityData,
    EntityDataStatus,
    LocalisableEntityData,
    ValidationService
} from "@fedeltapointofsale/data-client";
import {EntityFormBuilder} from "../entity-form-builder/entity-form-builder";
import {
    CommandColumnComponent,
    GridComponent,
    PageChangeEvent,
    RowClassArgs,
    RowClassFn
} from "@progress/kendo-angular-grid";
import {EditService} from "../edit-page/edit.service";
import {EntityFormCollectionControl} from "../entity-form-builder/entity-form-collection-control";
import {EditGridAddRowComponent} from "./edit-grid-add-row/edit-grid-add-row.component";
import {Subscription} from "rxjs";
import {LocalisationPageComponent} from "../../../../localisation/localisation-page/localisation-page.component";
import {
    LocalisationPageStackedParams
} from "../../../../localisation/localisation-page/localisation-page-stacked-params";
import {
    FUIAlertAction,
    FUIAlertActions,
    FUIAlertService,
    FUIEventsService,
    FUIFormContext,
    FUIInlineFormContext,
    FUIModalsService,
    FUIStackedPageService
} from "@fedeltapointofsale/ui";
import {EditGridReorderEvent} from "./edit-grid-reorder-event";
import {AddRowActionComponent} from "./edit-grid-add-row/edit-grid-add-row-actions/add-row-action.component";
import {
    EditGridAddRowNewEntityComponent
} from "./edit-grid-add-row/edit-grid-add-row-actions/edit-grid-add-row-new-entity.component";
import {
    EditGridAddRowSearchComponent
} from "./edit-grid-add-row/edit-grid-add-row-actions/edit-grid-add-row-search.component";
import {
    EditGridAddRowNewComponent
} from "./edit-grid-add-row/edit-grid-add-row-actions/edit-grid-add-row-new.component";
import {Config} from "../../../../config";
import {AnalyticsService} from "../../../../analytics/analytics.service";
import {AnalyticEvent} from "../../../../analytics/analytic-event";
import {EditGridCommandComponent} from "./edit-grid-command/edit-grid-command.component";
import {ActionEvent} from "../../action.event";
import {
    EditGridAddRowActionsComponent
} from "./edit-grid-add-row/edit-grid-add-row-actions/edit-grid-add-row-actions.component";
import {EditGridDragDropData} from "./edit-grid-reorder-drag-drop/edit-grid-reorder-drag-drop.component";
import {EditGridFormEditModalComponent} from "./edit-grid-form-edit/edit-grid-form-edit-modal.component";
import {TranslateService} from "@ngx-translate/core";
import {AbstractGridComponent} from "../../grid/abstract-grid.component";
import {EDIT_CONTROL_LABEL_CONTEXT} from "../edit-controls/abstract-edit-control.component";
import {
    EditGridAddRowCustomComponent
} from "./edit-grid-add-row/edit-grid-add-row-actions/edit-grid-add-row-custom.component";
import {EntityFormGroup} from "../entity-form-builder/entity-form-group";
import {EntityFormControl} from "../entity-form-builder/entity-form-control";
import {AbstractColumnComponent} from "../../grid/abstract-column.component";
import {CellActivationMode, EditGridService, WrappedBoolean, WrappedNumber, WrappedString} from "./edit-grid.service";
import {StringUtils} from "../../../utils/string-utils";

export declare type EditGridRowClassFn = (grid: EditGridComponent, context: RowClassArgs) => {
    [key: string]: any;
};

export enum SingleSelectStyle {
    RADIO,
    ARROW
}

/**
 * Directive to define a template for a cell
 */
@Directive({selector: "[gridFormTemplate]"})
export class GridFormTemplate {
}

/**
 * Grid component to be used in a edit.
 */
@Component({
    selector: "wbo-edit-grid",
    templateUrl: "./edit-grid.component.html",
    styleUrls: ["./edit-grid.component.scss"],
    providers: [EditGridService, {provide: FUIFormContext, useClass: FUIInlineFormContext}],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditGridComponent extends AbstractGridComponent implements OnInit, AfterViewInit, AfterContentInit, OnDestroy {
    SingleSelectStyle = SingleSelectStyle;
    StringUtils = StringUtils;

    public addRowActions: AddRowActionComponent[];
    public addRowNewEntityActions: EditGridAddRowNewEntityComponent[];
    public addRowSearchActions: EditGridAddRowSearchComponent[];
    public addRowNewActions: EditGridAddRowNewComponent[];
    public addRowCustomActions: EditGridAddRowCustomComponent[];
    public rowActionsSubscription: Subscription = new Subscription();
    @Input() localisable: boolean;
    @ContentChildren(EditGridAddRowComponent) addRowComponent: QueryList<EditGridAddRowComponent>;
    @ContentChild(EditGridCommandComponent) editGridCommandComponent: EditGridCommandComponent;
    @ContentChild(GridFormTemplate, {read: TemplateRef}) public gridFormTemplateRef: ElementRef;
    @Input() public title: string;
    /**
     * entityPath is used to create the validations and determine the placeholder keys
     */
    @Input() entityPath: string;
    @Input() editable: boolean = false;
    @Input() selectable: boolean = false;
    @Input() multiSelect: boolean = false;
    @Input() singleSelectStyle: SingleSelectStyle = SingleSelectStyle.RADIO;
    @Input() radioName: string;
    @Input() forcePageable: boolean = false;
    // determine that the grid will be pageable when the records go over a number of records
    @Input() pageSize: number = 50;
    @Input() scrollable: boolean = false;
    @Input() height: number = undefined;
    @Input() removable: boolean = false;
    @Input() showRemoveAtStart: boolean = false;
    @Input() showRemoveAtEnd: boolean = true;
    @Input() duplicatable: boolean = false;
    @Input() confirmRemoval: boolean = true;
    @Input() customRemovable: boolean = false;
    @Input() reorderable: boolean = false;
    @Input() emptyTitle: string = "wbo.edit.grid.add.empty.title";
    @Input() emptyDescription: string = "wbo.edit.grid.add.empty.description";
    @Input() rowClass: EditGridRowClassFn;
    @Input() showHeader: boolean = true;
    @Input() removeHeading: string = "wbo.search.prompt.remove.heading";
    @Input() removeMessage: string = "wbo.search.prompt.remove.message";
    @Input() showEditRowFormOnAdd: boolean = true;
    @Input() showEditAtStart: boolean = false;
    @Input() showEditAtEnd: boolean = true;
    @Input() editFormDataAssigner: (dataItem: EntityData<any>, editEntity: EntityData<any>) => boolean;
    // when it doesn't show the horizontal scroll it can have problems with performance. This flag sets to  always show the scroll improving performance.
    @Input() forceScroll: boolean;
    @Input() navOnShiftArrow: boolean = false;
    @Input() loading: boolean = false;
    @Input() respectRestrictedFormControls: boolean = false;
    @Input() standalone: boolean = false;
    private _readonly: boolean;

    /**
     * When not using a model this controlsConfig can be used to create the formGroup
     */
    @Input() controlsConfig: any;

    @Input() isEntityData: boolean = true;

    @Input() @HostBinding("attr.id") elementId: string;

    @Input() startCommandColumnWidth: number = 34;
    @Input() endCommandColumnWidth: number = 150;

    /**
     * Define the field names that will be used to create the formGroup when editing.
     */
    @Input() formColumnNames: string[];
    @Input() editFormFieldNames: string[];

    /**
     * Define if the grid is readonly
     */
    gridReadOnly: boolean;
    modelReadOnly: boolean;
    localisationEnabled: boolean;
    /**
     * Event emitted when a record is selected
     */
    @Output() selected: EventEmitter<any> = new EventEmitter<any>();

    /**
     * Event emitted when a record is selected in multi select mode. The event contains the list of all selected records
     */
    @Output() multiSelected: EventEmitter<any> = new EventEmitter<any>();
    /**
     * Can be used to prevent editing a row
     */
    @Output() editing: EventEmitter<ActionEvent> = new EventEmitter<ActionEvent>();
    /**
     * Event emitted when a record is saved
     * @type {EventEmitter<any>}
     */
    @Output() saved: EventEmitter<any> = new EventEmitter<any>();
    /**
     * Event emitted when a record is removed
     * @type {EventEmitter<any>}
     */
    @Output() removed: EventEmitter<any> = new EventEmitter<any>();
    /**
     * Event emitted when a record is reordered
     * @type {EventEmitter<>}
     */
    @Output() reordering: EventEmitter<EditGridReorderEvent> = new EventEmitter<EditGridReorderEvent>();

    @Output() droppedOther: EventEmitter<EditGridDragDropData> = new EventEmitter<EditGridDragDropData>();

    @Output() customRemoving: EventEmitter<any> = new EventEmitter<any>();

    @Output() duplicatingRow: EventEmitter<any> = new EventEmitter<any>();

    @Output() editFormClosed: EventEmitter<boolean> = new EventEmitter<boolean>();

    editingFormGroup: FormGroup;
    EditingFormGroupSubscription: Subscription;
    formCollectionControl: AbstractControl;
    public editingRowIndex: number;
    // single selected
    private _selectedRowIndex: number;
    // multi selected
    private _selectedMultiSelectRecords: EntityData<any>[] = [];
    editingColumnIndex: number;
    cellActivationMode: CellActivationMode;
    gridPageableSkip: number = 0;
    public editingRecord: EntityData<any> | any;
    private editingLocalisationRecord: LocalisableEntityData<any>;
    private clickOutsideSubscription: Subscription;
    addingRow: boolean = false;

    private _gridComponent: GridComponent;
    private _gridComponentElementRef: ElementRef;
    private gridData: any[];

    private labelContext: string;

    uuid: string = StringUtils.uuid();

    gridFocusEditElementFunction: any;

    @Input() rowRemovable: (rowIndex: number, dataItem: EntityData<any> | any) => boolean = () => true;

    @Input() rowDuplicatable: (rowIndex: number, dataItem: EntityData<any> | any) => boolean = () => true;

    constructor(injector: Injector,
                @Optional() public model: DataResourceModel<any>,
                @Optional() private editService: EditService,
                private entityFormBuilder: EntityFormBuilder,
                private formBuilder: FormBuilder,
                changeDetectorRef: ChangeDetectorRef,
                private editGridService: EditGridService,
                private stackedPageService: FUIStackedPageService,
                private alertService: FUIAlertService,
                private validationService: ValidationService,
                private eventsService: FUIEventsService,
                private analyticsService: AnalyticsService,
                translateService: TranslateService,
                configService: ConfigurationService,
                private modalsService: FUIModalsService) {
        super(changeDetectorRef, translateService, configService);

        this.labelContext = injector.get(EDIT_CONTROL_LABEL_CONTEXT, null);
        this.addRowActions = [];
        this.addRowNewEntityActions = [];
        this.addRowSearchActions = [];
        this.addRowNewActions = [];
        this.addRowCustomActions = [];
        this.editGridService.editGrid = this;
        if (this.model) {
            this.subscriptions.add(this.model.$readonlyChange.subscribe((readonly) => {
                this.modelReadOnly = readonly;
                this.evaluateGridReadOnly();
            }));
        }

        this.configService.getBoolean(Config.LOCALISATION).then((enabled) => this.localisationEnabled = enabled);
    }

    @ViewChild(GridComponent)
    set gridComponent(value: GridComponent) {
        this._gridComponent = value;

        // Disable kendo-grid auto-focus on edit activation
        if (this._gridComponent) {
            this.gridFocusEditElementFunction = this._gridComponent["focusEditElement"];
            this._gridComponent["focusEditElement"] = () => {
            };
        }
    }

    get gridComponent(): GridComponent {
        return this._gridComponent;
    }

    @ViewChild(GridComponent, {read: ElementRef})
    set gridComponentElementRef(value: ElementRef) {
        this._gridComponentElementRef = value;
    }

    get gridComponentElementRef(): ElementRef {
        return this._gridComponentElementRef;
    }

    @Input()
    get readonly(): boolean {
        return this._readonly;
    }

    set readonly(value: boolean) {
        this._readonly = value;
        this.evaluateGridReadOnly();
    }

    get data(): any[] {
        return this.editGridService.data;
    }

    @Input()
    set data(value: any[]) {
        this.editGridService.data = value;
        this.initData();
    }

    get gridDataView(): any {
        if (this.gridPageable) {
            return {
                data: this.gridData.slice(this.gridPageableSkip, this.gridPageableSkip + this.pageSize),
                total: this.gridData.length
            };
        } else {
            return this.gridData;
        }
    }

    get gridPageable(): boolean {
        return (this.forcePageable || !this.editable) && !!this.gridData && this.gridData.length > this.pageSize;
    }

    get pageData(): any[] {
        if (this.gridPageable) {
            return this.gridData.slice(this.gridPageableSkip, this.gridPageableSkip + this.pageSize);
        } else {
            return this.gridData;
        }
    }

    @Input()
    get selectedRowIndex(): number {
        return this._selectedRowIndex;
    }

    set selectedRowIndex(selectedRowIndex: number) {
        if (this.selectable && this._selectedRowIndex >= 0 && this._selectedRowIndex < this.gridData.length) {
            this.evaluateEntity(this.gridData[this._selectedRowIndex], this._selectedRowIndex);
        }

        this._selectedRowIndex = selectedRowIndex;
        if (this.selectable) {
            this.selected.emit(this._selectedRowIndex >= 0 ? (this.editGridService.primitive ? this.gridData[this._selectedRowIndex].value : this.gridData[this._selectedRowIndex]) : undefined);
        }
        this.changeDetectorRef.detectChanges();
    }

    get rowClassCallback(): RowClassFn {
        return (context: RowClassArgs) => {
            let result: { [key: string]: any } = this.rowClass ? this.rowClass(this, context) || {} : {};
            if (this.editingRowIndex !== context.index) {
                result = {
                    ...{
                        error: context.dataItem[EntityFormCollectionControl.FLAG_CHILD_ERROR]
                    }, ...result
                };
            }
            return result;
        }
    }

    public detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }

    ngOnInit(): void {
        if (!this.persistenceId && !this.standalone) {
            this.persistenceId = "edit." + this.editGridService.model.getResource() + "." + this.entityPath;
        }

        if (this.editService) {
            this.formCollectionControl = this.editService.formGroup.get(this.entityPath);
            if (this.formCollectionControl && this.formCollectionControl instanceof EntityFormCollectionControl) {
                this.editService.registerFormControl(this.formCollectionControl);
                this.subscriptions.add(this.formCollectionControl.$statusChange.subscribe(() => this.evaluateGridReadOnly()));
            }
        }

        if (!this.elementId && this.entityPath) {
            this.elementId = "edit-grid-" + this.entityPath.replace(/\./g, "-");
        }
    }

    ngAfterViewInit(): void {
        this.evaluateRowActions();
        this.subscriptions.add(this.addRowComponent.changes.subscribe(() => this.evaluateRowActions()));
        this.initData();
    }

    ngAfterContentInit(): void {
        this.evaluateHideableColumns();
        this.columns.changes.subscribe(() => {
            this.evaluateHideableColumns();
            this.changeDetectorRef.detectChanges();
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.rowActionsSubscription.unsubscribe();
        this._selectedRowIndex = undefined;
        if (this.selectable) {
            this.selected.emit(undefined);
        }
    }

    refresh(): void {
        this.changeDetectorRef.detectChanges();
    }

    initData(): void {
        this.editGridService.isEntityData = this.isEntityData;
        this.editGridService.primitive = this.isEntityData && this.entityPath && !this.model.data.getEntityDataType(this.entityPath);

        this.refreshGridData();

        if (this.multiSelect && this.selectable && this.editGridService.data) {
            this.evaluateAllSelected();
        }
    }

    evaluateRowActions(): void {
        this.addRowActions = [];
        this.addRowNewEntityActions = [];
        this.addRowSearchActions = [];
        this.addRowNewActions = [];
        this.addRowCustomActions = [];

        this.rowActionsSubscription.unsubscribe();
        this.rowActionsSubscription = new Subscription();

        if (this.addRowComponent.length) {
            const actionsContainer: EditGridAddRowActionsComponent = this.addRowComponent.first;
            this.rowActionsSubscription.add(actionsContainer.actions.changes.subscribe(() => {
                this.evaluateRowActions();
            }));
            this.addRowActions = this.addRowComponent.first["actions"].toArray();
            this.addRowActions.forEach((action) => {
                this.rowActionsSubscription.add(action.availabilityChanged.subscribe(() => {
                    this.changeDetectorRef.detectChanges();
                }));
                if (action instanceof EditGridAddRowNewEntityComponent) {
                    this.addRowNewEntityActions.push(action);
                } else if (action instanceof EditGridAddRowSearchComponent) {
                    this.addRowSearchActions.push(action);
                } else if (action instanceof EditGridAddRowNewComponent) {
                    this.addRowNewActions.push(action);
                } else if (action instanceof EditGridAddRowCustomComponent) {
                    this.addRowCustomActions.push(action);
                }
            });
        }

        this.changeDetectorRef.detectChanges();
    }

    evaluateGridReadOnly(): void {
        this.gridReadOnly = this.readonly || this.modelReadOnly ||
            (this.formCollectionControl && this.formCollectionControl instanceof EntityFormCollectionControl && this.formCollectionControl.restricted);
        this.changeDetectorRef.markForCheck();
    }

    protected override onVisibleColumnsChanged(): void {
        if (this.respectRestrictedFormControls && this.formCollectionControl && this.formCollectionControl instanceof EntityFormCollectionControl) {
            const formPath: string = this.formCollectionControl.path;
            this.visibleColumns.forEach((col: AbstractColumnComponent) => {
                const formControl: EntityFormControl = (<EntityFormGroup>this.formCollectionControl.root).findEntityFormControl(formPath + "." + col.field);
                if (formControl) {
                    col.restricted = formControl.restricted;
                }
            });
        }
    }

    public refreshGridData(): void {
        if (this.editGridService.primitive) {
            this.gridData = this.editGridService.data.map((primitive: any) => ({value: primitive}));
        } else {
            this.gridData = this.editGridService.data;
        }

        if (this._selectedRowIndex !== undefined) {
            this._selectedRowIndex = undefined;
            if (this.selectable) {
                this.selected.emit(undefined);
            }
        }
    }

    isEntityDataRestricted(entity: any): boolean {
        if (!this.model) {
            return false;
        }
        // if the model is not restricted don't restrict the entities
        if (this.model.data instanceof LocalisableEntityData) {
            if (!this.model.data.localisableContext.restricted) {
                return false;
            }
        }
        // when adding the entity is not an EntityData
        if (entity instanceof EntityData) {
            let localiser: EntityData<any> = entity.getLocaliser();
            if (!localiser && entity instanceof LocalisableEntityData) {
                localiser = entity;
            }
            return localiser && (localiser instanceof LocalisableEntityData) ? localiser.localisableContext.restricted : false;
        } else {
            return false;
        }
    }

    /**
     * Create an instance to be added to the data
     * @returns {EntityData<any>}
     */
    createEntity(): EntityData<any> | any {
        if (this.editGridService.primitive) {
            return {value: undefined};
        } else {
            return this.model.data.createChildInstance(this.entityPath);
        }
    }

    selectRow(rowIndex: number, dataItem: EntityData<any> | any): void {
        this.confirmEditing();
        this.selectedRowIndex = rowIndex;
    }

    /**
     * Add a row to the grid collection and return the new size of the collection
     * @param {EntityData<any>} entityData
     * @returns {number}
     */
    addRow(entityData: EntityData<any> | any): number {
        this.addingRow = true;
        setTimeout(() => this.addingRow = false, 500);

        if (this.editService) {
            this.editService.dirty = true;
        }
        if (this.editGridService.primitive) {
            this.editGridService.data.push(entityData.value);
            this.refreshGridData();
        } else {
            this.editGridService.data.push(entityData);
            this.refreshGridData();
        }
        if (this.editable) {
            this.evaluateEntity(entityData, this.editGridService.data.length - 1);
        }
        this.changeDetectorRef.markForCheck();
        return this.editGridService.data.length;
    }

    editRow(dataItem: EntityData<any> | any, rowIndex: number, isNewRow: boolean = false, cellActivationMode: CellActivationMode = CellActivationMode.FOCUS, columnIndex?: number): void {
        if (this.editingRecord) {
            this.saveRow(this.editingRowIndex, this.editingFormGroup);
        }

        const editingAction: ActionEvent = new ActionEvent(dataItem);

        this.editing.next(editingAction);

        if (editingAction.isDefaultPrevented()) {
            return;
        }

        if (this.isEntityDataRestricted(dataItem)) {
            return;
        }

        if (this.editService) {
            this.editService.gridEditOpened();
        }

        this.editingRecord = dataItem;

        if (this.controlsConfig) {
            this.editingFormGroup = this.formBuilder.group(this.controlsConfig);
        } else {
            this.editingFormGroup = this.entityFormBuilder.groupCollection(this.model, this.model.data.convertToActualPath(this.entityPath), rowIndex, this.formColumnNames);
        }

        this.EditingFormGroupSubscription = this.editingFormGroup.statusChanges.subscribe(() => {
            if (this.editingFormGroup.pending) {
                // when is pending angular is failing to report when the status changes to valid
                setTimeout(() => {
                    if (this.editingFormGroup) {
                        this.editingFormGroup.updateValueAndValidity();
                    }
                });
            } else {
                this.editingRecord[EntityFormCollectionControl.FLAG_CHILD_ERROR] = !this.editingFormGroup.valid;
                this.changeDetectorRef.detectChanges();
            }
        });
        this.editingFormGroup.reset(dataItem);

        if (!isNewRow) {
            this.markControlsAsDirty(this.editingFormGroup);
        }

        this.editingRowIndex = rowIndex;
        if (this.multiSelect || this.singleSelectStyle === SingleSelectStyle.RADIO) {
            this.selectedRowIndex = rowIndex;
        }

        this._gridComponent.editRow(rowIndex, this.editingFormGroup);

        if (columnIndex === undefined) {
            columnIndex = this.columns.toArray().findIndex((column) => column.editable && !column.restricted);
        }

        if (columnIndex !== undefined) {
            if (cellActivationMode === CellActivationMode.FOCUS || this.columns.toArray()[columnIndex].autoClickOnRowEdit) {
                this.editGridService.setupCellActivation(cellActivationMode, columnIndex);
            } else if (this.editGridService.cellActivationMode === undefined) {
                this.editGridService.setupCellActivation(CellActivationMode.FOCUS, columnIndex);
            }
        }

        this.analyticsService.trackEvent(AnalyticEvent.ENTITY_INTERACTION_GRID_INLINE_EDIT, this.model);

        this.turnOnClickOutsideHandler();

        this.addingRow = false;

        this.changeDetectorRef.markForCheck();
    }

    addAndEditRow(entityData: EntityData<any>): void {
        const index: number = this.addRow(entityData) - 1;
        if (this.gridFormTemplateRef && this.showEditRowFormOnAdd) {
            this.editRowForm(entityData, index, true);
        } else {
            setTimeout(() => this.editRow(entityData, index, true));
        }
    }

    markControlsAsDirty(formGroup: FormGroup): void {
        for (const key in formGroup.controls) {
            if (formGroup.controls[key] instanceof FormGroup) {
                this.markControlsAsDirty(<FormGroup>formGroup.controls[key]);
            } else {
                formGroup.controls[key].markAsDirty({onlySelf: false});
            }
        }
    }

    saveRow(rowIndex: number, formGroup: FormGroup): void {
        const entity: any = formGroup.value;

        if (this.editGridService.primitive) {
            this.editingRecord.value = entity.value;
            this.editGridService.data[rowIndex] = entity.value;
        } else {
            this.columns.filter(column => column.editable && !column.restricted).forEach(column => {
                if (this.editingRecord instanceof EntityData) {
                    column.editingFields().forEach((field) => this.editingRecord.set(field, EntityData.getField(entity, field)));
                } else {
                    column.editingFields().forEach((field) => this.editingRecord[field] = entity[field]);
                }
            });
        }

        this.saved.emit(this.editingRecord);

        this._gridComponent.closeRow(rowIndex);

        if (this.editable) {
            this.evaluateEntity(this.editingRecord, rowIndex);
        }

        this.closeEditor(rowIndex);

        if (this.editService) {
            this.editService.dirty = true;
        }

        this.turnOffClickOutsideHandler();

        if (this.formCollectionControl) {
            this.formCollectionControl.markAsDirty();
        }
    }

    async removeRow(rowIndex: number, dataItem: EntityData<any> | any, confirm: boolean = true): Promise<void> {
        this.confirmEditing();

        if (this.customRemovable) {
            this.customRemoving.emit(dataItem);
            return;
        }

        const action: FUIAlertAction = confirm ? await this.alertService.showConfirmationOkCancel(this.removeHeading, this.removeMessage).toPromise() : FUIAlertActions.OK_ACTION;
        if (action === FUIAlertActions.OK_ACTION) {
            if (this.editGridService.primitive) {
                dataItem = this.editGridService.data[rowIndex];
            }
            this.editGridService.data.splice(rowIndex, 1);
            this.refreshGridData();

            this.removed.next(dataItem);

            if (this.editService) {
                this.editService.dirty = true;
            }
            this.analyticsService.trackEvent(AnalyticEvent.ENTITY_INTERACTION_GRID_ROW_REMOVE, this.model);

            if (this.formCollectionControl) {
                this.formCollectionControl.markAsDirty();
            }

            this.changeDetectorRef.markForCheck();
        }
    }

    async duplicateRow(rowIndex: number, dataItem: EntityData<any> | any, confirm: boolean = true): Promise<void> {
        this.confirmEditing();

        this.duplicatingRow.emit(dataItem);
    }

    cancelEditing(): void {
        if (this.editingRecord) {
            this.closeEditor();
            this.changeDetectorRef.detectChanges();
        }
    }

    confirmEditing(): void {
        if (this.editingRecord) {
            this.saveRow(this.editingRowIndex, this.editingFormGroup);
        }
    }

    editRowForm(dataItem: EntityData<any> | any, rowIndex: number, isNew: boolean): void {
        if (this.editingRecord) {
            this.confirmEditing();
        }

        let editEntity: EntityData<any>;
        if (isNew) {
            editEntity = dataItem;
        } else {
            editEntity = this.createEntity();
            editEntity.assign(dataItem, EntityDataStatus.LOADED, true, true);
        }

        if (!this.labelContext) {
            this.labelContext = this.editGridService.model.getResource();
        }

        this.modalsService.create(EditGridFormEditModalComponent).subscribe(modal => {
            const formTitle: string = "wbo." + this.labelContext + ".edit.editGridForm." + this.editGridService.editGrid.entityPath + (isNew ? ".addTitle" : ".editTitle");
            const formGroup: FormGroup = this.entityFormBuilder.groupCollection(this.model, this.model.data.convertToActualPath(this.entityPath), rowIndex, this.editFormFieldNames);
            this.editService.restrictControls(formGroup)

            formGroup.reset(editEntity);
            modal.setup(this.gridFormTemplateRef, formGroup, editEntity, formTitle);

            modal.onConfirm.subscribe((confirmed) => {
                if (confirmed) {
                    if (!isNew) {
                        if (this.editFormDataAssigner) {
                            if (this.editFormDataAssigner(dataItem, editEntity)) {
                                this.refreshGridData();
                            }
                        } else {
                            editEntity.markAsLoaded();
                            dataItem.assign(editEntity, EntityDataStatus.LOADED, true, true);
                            this.refreshGridData();
                        }
                    }
                    this.evaluateEntity(dataItem, rowIndex);
                    this.saved.emit(dataItem);
                    this.changeDetectorRef.detectChanges();
                } else {
                    if (isNew) {
                        this.removeRow(rowIndex, dataItem, false);
                    }
                }

                this.editFormClosed.emit(confirmed);

                this.modalsService.destroy(modal);

                this.evaluateFormCollectionControl();
            });
        });
    }

    trackByFn(index, item) {
        return item.field;
    }

    confirmLocalisation(localisationPageStackedParams: LocalisationPageStackedParams): void {
        this.editingLocalisationRecord.localisableChildContext.outlets = localisationPageStackedParams.selectedOutlets;
        this.editingLocalisationRecord.localisableChildContext.customObjects = localisationPageStackedParams.selectedCustomObjects;
        this.editingLocalisationRecord.localisableChildContext.universal = localisationPageStackedParams.universal;
    }

    openLocalisationDialog(dataItem: LocalisableEntityData<any>): void {
        this.editingLocalisationRecord = dataItem;

        this.subscriptions.add(this.stackedPageService.open(LocalisationPageComponent, new LocalisationPageStackedParams(true, dataItem.localisableChildContext.outlets,
            dataItem.localisableChildContext.customObjects, dataItem.localisableChildContext.universal))
            .subscribe((localisationPageStackedParams) => this.confirmLocalisation(localisationPageStackedParams)));

        this.analyticsService.trackEvent(AnalyticEvent.ENTITY_INTERACTION_GRID_LOCALISATION, this.model);
    }

    /**
     * Check if the outlet has been localised
     * @returns {boolean}
     */
    isLocalised(dataItem: LocalisableEntityData<any>): boolean {
        return !dataItem.localisableChildContext.universal;
    }

    public cellClickHandler({dataItem, rowIndex, columnIndex, column, originalEvent}: {
        dataItem: EntityData<any>,
        rowIndex: number,
        columnIndex: number,
        column: AbstractColumnComponent,
        originalEvent: Event
    }): void {
        if (!this.editable || this.gridReadOnly) {
            if (this.selectable) {
                if (this.multiSelect) {
                    this.selectMultiSelectRecord(!this.isSelected(dataItem), dataItem);
                } else {
                    this.selectedRowIndex = rowIndex;
                }
            }
            return;
        }

        // Click in error indicator - edit row with no column selected
        if (originalEvent.target instanceof Element && originalEvent.target.className === "error-indicator") {
            if (dataItem !== this.editingRecord) {
                this.editRow(dataItem, rowIndex, false, undefined, undefined);
            }
            return;
        }

        const leaderColumns: number =
            (this.reorderable ? 1 : 0) +
            (this.selectable && (this.multiSelect || this.singleSelectStyle === SingleSelectStyle.RADIO) ? 1 : 0) +
            (((this.gridFormTemplateRef && this.showEditAtStart) || (this.removable && this.showRemoveAtStart)) ? 1 : 0);
        if (columnIndex < leaderColumns || columnIndex >= this.columns.length + leaderColumns || column instanceof CommandColumnComponent) {
            return;
        }

        if (dataItem === this.editingRecord) {
            // Click in column outside of control - set focus to column
            this.editGridService.focusOnColumn(columnIndex - leaderColumns);
        } else {
            this.editRow(dataItem, rowIndex, false, CellActivationMode.CLICK, columnIndex - leaderColumns);
        }
    }

    keydownHandler(event: KeyboardEvent) {
        if (event.ctrlKey) {
            return;
        }
        if (event.code === "Enter" || event.key == "Enter" || (event.code === "ArrowDown" && (event.shiftKey || !this.navOnShiftArrow))) {
            if (this.editingRowIndex < this.editGridService.data.length - 1) {
                this.editRow(this.editGridService.data[this.editingRowIndex + 1], this.editingRowIndex + 1, false, CellActivationMode.FOCUS, this.editingColumnIndex);
            }
            event.preventDefault();
            event.stopPropagation();
        } else if (event.code === "ArrowUp" && (event.shiftKey || !this.navOnShiftArrow)) {
            if (this.editingRowIndex > 0) {
                this.editRow(this.editGridService.data[this.editingRowIndex - 1], this.editingRowIndex - 1, false, CellActivationMode.FOCUS, this.editingColumnIndex);
            }
            event.preventDefault();
            event.stopPropagation();
        } else if (event.code === "Escape") {
            this.saveRow(this.editingRowIndex, this.editingFormGroup);
            event.preventDefault();
            event.stopPropagation();
        }
    }

    clickOutsideHandler({ignoreClickOutside}: { ignoreClickOutside: boolean }) {
        if (!ignoreClickOutside && this.editingRecord) {
            this.saveRow(this.editingRowIndex, this.editingFormGroup);
        }
    }

    turnOnClickOutsideHandler(): void {
        if (this.clickOutsideSubscription) {
            this.turnOffClickOutsideHandler();
        }
        this.clickOutsideSubscription = this.eventsService.$click.subscribe((event: MouseEvent) => {
            if (!event["ignoreClickOutside"] && this.editingRecord && !this._gridComponentElementRef.nativeElement.contains(event.target)) {
                this.saveRow(this.editingRowIndex, this.editingFormGroup);
            }
        });
    }

    turnOffClickOutsideHandler(): void {
        if (this.clickOutsideSubscription) {
            this.clickOutsideSubscription.unsubscribe();
        }
        this.clickOutsideSubscription = null;
    }

    public revalidate(): void {
        let row: number = 0;
        this.gridData.forEach((entityData: EntityData<any> | any) => this.evaluateEntity(entityData, row++));
    }

    public evaluateEntity(entityData: EntityData<any> | any, rowIndex: number): void {
        if (!this.formCollectionControl) {
            return;
        }

        // mark the control pending while it validates
        this.formCollectionControl.markAsPending();
        const validationPath = this.model.data.convertToActualPath(this.entityPath);
        this.validationService.validateChild(this.model.data, entityData, validationPath, this.editingRowIndex == undefined ? rowIndex : this.editingRowIndex).then((validation) => {
            entityData[EntityFormCollectionControl.FLAG_CHILD_ERROR] = validation && validation.length !== 0;
            if (this.editGridService.primitive) {
                this.setChildErrorOnPrimitiveElement(rowIndex, entityData[EntityFormCollectionControl.FLAG_CHILD_ERROR]);
            }
            this.evaluateFormCollectionControl();
        }).catch(() => {
            entityData[EntityFormCollectionControl.FLAG_CHILD_ERROR] = true;
            if (this.editGridService.primitive) {
                this.setChildErrorOnPrimitiveElement(rowIndex, true);
            }
            this.evaluateFormCollectionControl();
        });
    }

    onReordering(event: EditGridReorderEvent): void {
        if (this.editingRowIndex) {
            this.saveRow(this.editingRowIndex, this.editingFormGroup);
        }
        this.reordering.emit(event)
        this.editService.dirty = true;
    }

    onDroppedOther(event: EditGridDragDropData): void {
        this.droppedOther.emit(event);
    }

    /**
     * Run the appropriate click handler in the provided
     * AddRowActionComponent
     * @param {AddRowActionComponent} addRowAction
     */
    public command(addRowAction: AddRowActionComponent): void {
        addRowAction.click();
    }

    onGridResize(): void {
        this.changeDetectorRef.detectChanges();
    }

    pageChange({skip, take}: PageChangeEvent): void {
        this.gridPageableSkip = skip;

        if (this.multiSelect && this.selectable) {
            this.evaluateAllSelected();
        }
    }

    closeEditor(rowIndex = this.editingRowIndex) {
        if (this.editingRecord) {
            this._gridComponent.closeRow(rowIndex);
            this.editingRowIndex = undefined;

            this.EditingFormGroupSubscription.unsubscribe();
            this.editingFormGroup = undefined;

            this.editingRecord = undefined;

            if (this.editService) {
                this.editService.gridEditClosed();
            }
        }
    }

    private setChildErrorOnPrimitiveElement(rowIndex: number, childError: boolean) {
        let wrapped: any;
        if (this.editGridService.data[rowIndex] == undefined) {
            wrapped = new WrappedString(undefined);
        } else if (typeof this.editGridService.data[rowIndex] === "string") {
            wrapped = new WrappedString(this.editGridService.data[rowIndex]);
        } else if (typeof this.editGridService.data[rowIndex] === "number") {
            wrapped = new WrappedNumber(this.editGridService.data[rowIndex]);
        } else if (typeof this.editGridService.data[rowIndex] === "boolean") {
            wrapped = new WrappedBoolean(this.editGridService.data[rowIndex]);
        } else {
            wrapped = this.editGridService.data[rowIndex];
        }
        wrapped[EntityFormCollectionControl.FLAG_CHILD_ERROR] = childError;
        this.editGridService.data[rowIndex] = wrapped;
    }

    private evaluateFormCollectionControl(): void {
        this.formCollectionControl.updateValueAndValidity();
    }

    isSelected(dataItem: EntityData<any>): boolean {
        const index: number = this._selectedMultiSelectRecords.findIndex(record => record.getIdentifier(false) === dataItem.getIdentifier(false));
        return index !== -1;
    }

    selectMultiSelectRecord(checked: boolean, selectedRecord: EntityData<any>): void {
        const selectedIdentifier: any = selectedRecord.getIdentifier(false);
        const index: number = this._selectedMultiSelectRecords.findIndex(record => record.getIdentifier(false) === selectedIdentifier);
        if (checked) {
            if (index === -1) {
                this._selectedMultiSelectRecords.push(selectedRecord);
            }
        } else if (index !== -1) {
            this._selectedMultiSelectRecords.splice(index, 1);
        }
        this.multiSelected.emit(this._selectedMultiSelectRecords);

        this.evaluateAllSelected();
    }

    get selectedMultiSelectRecords(): EntityData<any>[] {
        return this._selectedMultiSelectRecords;
    }

    set selectedMultiSelectRecords(value: EntityData<any>[]) {
        this._selectedMultiSelectRecords = value;
    }

    clearMultiSelectedRecords(): void {
        this._selectedMultiSelectRecords = [];
        this.allSelected = false;
        this.changeDetectorRef.detectChanges();
    }

    toggleSelectAll(checked: boolean): void {
        if (this.allSelected != checked) {
            this.allSelected = checked;
            this.pageData.forEach((dataRecord) => {
                const selectedIdentifier: any = dataRecord.getIdentifier(false);
                const index: number = this._selectedMultiSelectRecords.findIndex(record => record.getIdentifier(false) === selectedIdentifier);
                if (checked) {
                    if (index === -1) {
                        this._selectedMultiSelectRecords.push(dataRecord);
                    }
                } else if (index !== -1) {
                    this._selectedMultiSelectRecords.splice(index, 1);
                }
            });
            this.multiSelected.emit(this._selectedMultiSelectRecords);
        }
    }

    evaluateAllSelected(): void {
        let all: boolean = true;
        this.pageData.some((record: any) => {
            if (!this.isSelected(record)) {
                all = false;
                return true;
            }
            return false;
        });
        this.allSelected = all;
    }

    focusOnRowIndex(rowIndex: number): void {
        this.gridFocusEditElementFunction.apply(this._gridComponent, [`tr[data-kendo-grid-item-index="${this.editGridService.editGrid.selectedRowIndex}"]`]);
    }
}
